<template>
  <AppSection class="news-preview" :data="{ img: '' }">
    <AppHeadline
      class="news-preview__title"
      :data="{
        content: title,
        type: 2,
      }"
      v-if="title !== ''"
    />
    <div class="news-preview__container">
      <router-link
        class="news-preview__card"
        v-for="(item, i) in items"
        :key="i"
        :to="`/insights${item.path}`"
      >
        <div
          class="news-preview__image"
          :style="{
            backgroundImage: `url(${item.acf.image.url})`
          }"
        ></div>
        <AppSubheading
          class="news-preview__eyelet"
          :data="{
            content: item.acf.category.name,
          }"
        />
        <AppParagraph
          :data="{
            content: item.title,
          }"
        />
      </router-link>
    </div>
    <router-link to="/insights" class="news-preview__button">
      <AppButton v-if="haveButton" :data="{ content: 'SEE ALL' }" />
    </router-link>
  </AppSection>
</template>

<script>
import { mapGetters } from 'vuex';
import { TimelineMax, Power4 } from 'gsap/TweenMax';
import raf from '@/scripts/raf';

import AppSection from '@/components/AppSection.vue';
import AppHeadline from '@/components/AppHeadline.vue';
import AppSubheading from '@/components/AppSubheading.vue';
import AppParagraph from '@/components/AppParagraph.vue';
import AppButton from '@/components/AppButton.vue';

export default {
  name: 'newsPreview',
  components: {
    AppSection,
    AppHeadline,
    AppSubheading,
    AppParagraph,
    AppButton,
  },
  props: {
    title: {
      type: String,
      required: false,
      default: '',
    },
    haveButton: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      items: [],
      animationAdded: false,
    };
  },
  computed: {
    ...mapGetters({ news: 'getNews' }),
  },
  mounted() {
    if (this.news.firstItems.length === 0) {
      this.$store.dispatch('FETCH_NEWS').then(() => {
        this.items = this.news.firstItems.slice(0, 3);
        this.animationAdded = true;
        raf.add(() => this.enter(), 'animation-preview');
      });
    } else {
      this.items = this.news.firstItems.slice(0, 3);
      this.animationAdded = true;
      raf.add(() => this.enter(), 'animation-preview');
    }
  },
  methods: {
    enter() {
      if (this.$el.getBoundingClientRect().top < (window.innerHeight / 2) && this.animationAdded) {
        this.animationAdded = false;
        raf.remove('animation-preview');

        const tl = new TimelineMax();
        tl.addLabel('start')
          .fromTo('.news-preview__title', 1, {
            autoAlpha: 0,
            y: 60,
          }, {
            autoAlpha: 1,
            y: 0,
            ease: Power4.easeOut,
          }, 'start')
          .staggerFromTo('.news-preview__card', 1, {
            autoAlpha: 0,
            y: 60,
          }, {
            autoAlpha: 1,
            y: 0,
            ease: Power4.easeOut,
          }, 0.3, 'start+=0.5')
          .fromTo('.news-preview__button', 1, {
            autoAlpha: 0,
            y: 60,
          }, {
            autoAlpha: 1,
            y: 0,
            ease: Power4.easeOut,
          }, 'start+=1.5');
      }
    },
  },
  beforeDestroy() {
    if (this.animationAdded) raf.remove('animation-preview');
  },
};
</script>

<style lang="scss" scoped>
.news-preview {
  align-items: center;
  justify-content: center;
  flex-flow: column nowrap;

  background-image: linear-gradient(180deg, #9EA0AC 0%, #141B37 100%);

  &__image {
    width: 100%;
    margin-bottom: 10px;

    background-color: rgb(74, 79, 95);
    background-size: cover;
    background-position: center center;
    background-repeat: no-repeat;

    @include aspect-ratio(1, 1);

    transition: opacity 0.4s $outQuint;

    @include mq($until: ipadP) {
      display: none;
    }
  }

  &__container {
    width: 100%;
    padding: 60px 0;

    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    flex-flow: row wrap;

    @include mq($until: ipadP) {
      padding: 80px 0;
    }

    > a {
      width: calc(33.3% - 30px);
      flex: 0 0 calc(33.3% - 30px);
      border-bottom: 1px solid $color-white;
      padding-bottom: 20px;
      opacity: 0;

      &:hover .news-preview__image {
        opacity: 0.5;
      }

      @include mq($until: ipadP) {
        width: 100%;
        flex: 0 0 100%;

        margin-bottom: 40px;

        &:last-child {
          margin-bottom: 0;
        }
      }
    }
  }

  &__title,
  &__button {
    display: block;
    opacity: 0;
  }

  &__eyelet {
    margin-bottom: 15px;
  }
}
</style>
